'use client';

import { useState, useEffect, Suspense, useCallback, useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';
import { useSecureAuthContext } from '../../../core/auth/services';
import { useLoginMutation } from '@/api/features/auth';
import { authLogger } from '../../../core/utils/logger';
import { useGlobalLoading } from '../../../core/providers/GlobalLoadingProvider';
import { useZodForm, loginSchema, type LoginFormData } from '@/shared/validation';
import { getUserFriendlyError } from '@voley/shared-utils';
import { isRtkQueryError } from '@voley/shared-types/api';
import { extractErrorMessage } from '@/shared/utils/error';
import { useAppInitialization } from '@/core/providers/AppInitializationProvider';
import { withTimeout } from '@voley/shared-utils/with-timeout';
import { setGlobalCsrfToken } from '@/core/providers/CsrfProvider';
import { BRAND_BLUE } from '@voley/shared-types/constants';
import { runConnectivityDiagnostic } from '@/shared/utils/connectivityDiagnostic';
import * as Sentry from '@sentry/react';
import { STEP_TO_ROUTE_MAP } from '@/features/onboarding/hooks/useOnboardingSession';

// --- Custom hook: extracts login logic from the component ---
function useLoginHandler() {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showLoading, hideLoading } = useGlobalLoading();
  const [loginMutation] = useLoginMutation({ fixedCacheKey: 'login-mutation-fixed' });
  const { isReady: appReady, waitUntilReady, error: initError } = useAppInitialization();
  const searchParams = useSearchParams();
  const { logout } = useSecureAuthContext();

  const returnUrl = searchParams.get('returnUrl') || '/dashboard';
  const forceLogout = searchParams.get('force_logout') === 'true';
  const messageParam = searchParams.get('message');

  const sessionMessage = useMemo(() => {
    if (messageParam === 'session_replaced') return 'Your account was signed in from another location. Only one active session is allowed. Please sign in again.';
    if (messageParam === 'session_expired') return 'Your session has expired. Please sign in again to continue.';
    if (messageParam === 'session_timeout') return 'Your session timed out. Please sign in again to continue.';
    return null;
  }, [messageParam]);

  useEffect(() => {
    authLogger.info('[LoginPage] Initialization status:', { appReady, initError });
  }, [appReady, initError]);

  useEffect(() => {
    if (forceLogout) {
      authLogger.info('Force logout requested, clearing all auth data...');
      logout().then(() => {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('force_logout');
        window.history.replaceState({}, '', newUrl);
      });
    }
  }, [forceLogout, logout]);

  const handleLogin = useCallback(async (data: LoginFormData) => {
    authLogger.debug('Starting login process', { email: data.email, appReady });
    setError(null);
    setIsSubmitting(true);

    try {
      if (!appReady) {
        authLogger.debug('Waiting for app initialization (max 5 seconds)...');
        try {
          await withTimeout({ promise: waitUntilReady(), ms: 5000, message: 'App initialization timeout reached' });
          authLogger.debug('App initialization complete');
        } catch (initErr) {
          authLogger.warn('App initialization timeout, proceeding with login anyway:', initErr);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 100));
      showLoading('Signing you in...');

      authLogger.debug('Calling login mutation');
      const result = await withTimeout({
        promise: loginMutation({ email: data.email, password: data.password, clientType: 'web' }).unwrap(),
        ms: 15000, message: 'Login request timed out. Please check your connection and try again.',
      });

      authLogger.debug('Login response', {
        hasUser: !!result.user,
        hasSessionTokens: !!result.session?.sessionToken && !!result.session?.sessionJwt,
        hasOnboarding: !!result.onboardingSession,
      });

      if (result.user && result.session?.sessionToken) {
        authLogger.info('Login successful', { userId: result.user.id, email: result.user.email });

        if (process.env.NODE_ENV === 'development') {
          authLogger.debug('[LOGIN_DEBUG] Login success, checking cookies...', {
            allCookies: document.cookie, hasJwtAccess: document.cookie.includes('jwt_access='),
            hasJwtRefresh: document.cookie.includes('jwt_refresh='), hasStytchSession: document.cookie.includes('stytch_session='),
            hasCsrfToken: document.cookie.includes('csrf-token='),
          });
        }

        authLogger.debug('[Login] Waiting for browser to process login cookies...');
        const maxWaitMs = 5000;
        const pollIntervalMs = 50;
        const startTime = Date.now();
        const getCsrfFromCookie = (): string | null => {
          const match = document.cookie.match(/(?:^|; )csrf-token=([^;]+)/);
          if (match) { try { return decodeURIComponent(match[1]); } catch { return match[1]; } }
          return null;
        };

        let csrfToken: string | null = null;
        while (!csrfToken && (Date.now() - startTime) < maxWaitMs) {
          csrfToken = getCsrfFromCookie();
          if (!csrfToken) await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
        }

        if (csrfToken) {
          setGlobalCsrfToken(csrfToken);
          authLogger.debug('[Login] CSRF cookie ready after', Date.now() - startTime, 'ms');
        } else {
          authLogger.warn('[Login] Cookie polling timed out after', maxWaitMs, 'ms, proceeding anyway');
        }

        sessionStorage.setItem('auth_state_cache', JSON.stringify({ user: result.user, timestamp: Date.now() }));
        authLogger.debug('[Login] Auth state cached to sessionStorage');

        const hasOnboardingData = result.onboardingSession;
        const onboardingStatus = result.onboardingSession?.status;
        const currentStep = result.onboardingSession?.current_step;
        authLogger.debug('Onboarding check', { hasOnboardingData, onboardingStatus, currentStep });

        let redirectTo = '/dashboard';
        try {
          const decoded = decodeURIComponent(returnUrl || '');
          if (decoded && decoded.startsWith('/')) redirectTo = decoded;
        } catch { /* ignore malformed returnUrl */ }

        if (hasOnboardingData && onboardingStatus === 'in_progress' && currentStep && currentStep !== 'setup_complete' && returnUrl.includes('dashboard')) {
          const onboardingRoute = STEP_TO_ROUTE_MAP[currentStep];
          if (onboardingRoute) { redirectTo = onboardingRoute; authLogger.info('Redirecting to onboarding step', { route: redirectTo, currentStep }); }
        } else if (currentStep === 'setup_complete' || onboardingStatus === 'completed') {
          redirectTo = '/dashboard';
          authLogger.info('Onboarding complete, redirecting to dashboard');
        }

        if (process.env.NODE_ENV === 'development') {
          authLogger.debug('[LOGIN_DEBUG] About to redirect', {
            redirectTo, allCookies: document.cookie, hasJwtAccess: document.cookie.includes('jwt_access='),
            hasJwtRefresh: document.cookie.includes('jwt_refresh='), hasStytchSession: document.cookie.includes('stytch_session='),
            hasCsrfToken: document.cookie.includes('csrf-token='), sessionStorageKeys: Object.keys(sessionStorage),
          });
        }
        authLogger.info('Final redirect', { route: redirectTo });
        if (typeof window !== 'undefined') window.location.href = redirectTo;
      } else {
        hideLoading(); setIsSubmitting(false);
        authLogger.error('Login response missing required data');
        setError('Login failed. Please try again.');
      }
    } catch (err: unknown) {
      hideLoading(); setIsSubmitting(false);

      if (err instanceof Error && err.message.includes('initialization')) {
        authLogger.error('App initialization failed:', err);
        setError('System initialization failed. Please refresh the page and try again.');
        return;
      }

      authLogger.error('Login error details:', { error: err, errorType: typeof err, errorMessage: extractErrorMessage(err, 'Unknown'), appReady });

      const friendlyError = isRtkQueryError(err)
        ? getUserFriendlyError(err) : getUserFriendlyError(err instanceof Error ? err : new Error('Unknown error'));

      const isNetworkError = (
        (isRtkQueryError(err) && (err.status === 'FETCH_ERROR' || err.status === 'TIMEOUT_ERROR')) ||
        (err instanceof Error && (err.message.includes('timed out') || err.message.includes('fetch')))
      );

      if (isNetworkError) {
        runConnectivityDiagnostic().then((diagnostic) => {
          Sentry.captureMessage('Login network error', {
            level: 'error',
            tags: { api_reachable: String(diagnostic.apiReachable), browser_online: String(diagnostic.browserOnline),
              cookies_enabled: String(diagnostic.cookiesEnabled), has_csrf_cookie: String(diagnostic.hasCsrfCookie) },
            extra: { diagnostic, originalError: extractErrorMessage(err) },
          });
        }).catch(() => { /* diagnostic failed, ignore */ });
      }

      const supportSuffix = friendlyError.supportId ? ` (Support ID: ${friendlyError.supportId})` : '';
      setError(`${friendlyError.message}${supportSuffix}`);
    }
  }, [appReady, waitUntilReady, loginMutation, showLoading, hideLoading, returnUrl]);

  return { error, setError, isSubmitting, handleLogin, sessionMessage, appReady, initError };
}

function LoginPageContent() {
  const [showPassword, setShowPassword] = useState(false);
  const { error, setError, isSubmitting, handleLogin, sessionMessage, appReady, initError } = useLoginHandler();
  const searchParams = useSearchParams();
  const messageParam = searchParams.get('message');

  const form = useZodForm(loginSchema);
  const { register, handleSubmit, formState: { errors } } = form;


  // Brand color matching external pages
  const brandBlue = BRAND_BLUE;
  
  return (
    <>
      <div className="mb-8 text-center">
        <h2 
          className="text-2xl font-semibold tracking-tight"
          style={{ color: '#111827' }}
        >
          Welcome back
        </h2>
        <p 
          className="mt-2 text-base"
          style={{ color: '#6B7280' }}
        >
          Sign in to access your account
        </p>
      </div>

      {(error || sessionMessage) && (
        <div
          className="mb-6 p-4 rounded-xl text-sm"
          data-testid="login-error"
          style={
            messageParam === 'session_replaced' && !error
              ? { backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }
              : { backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }
          }
        >
          {error || sessionMessage}
        </div>
      )}

      <form onSubmit={handleSubmit(handleLogin as unknown as SubmitHandler<Record<string, unknown>>)} className="space-y-5" noValidate data-testid="login-form">
        {/* Email */}
        <div>
          <label 
            htmlFor="email" 
            className="block text-sm font-medium mb-2"
            style={{ color: '#374151' }}
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            data-testid="login-email-input"
            {...register('email', {
              onChange: () => {
                if (error) setError(null);
              }
            })}
            className="w-full px-4 py-3 rounded-xl text-base transition-all duration-200"
            style={{
              backgroundColor: '#F9FAFB',
              border: errors.email ? '1px solid #DC2626' : '1px solid #E5E7EB',
              color: '#111827',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = brandBlue;
              e.target.style.boxShadow = `0 0 0 3px ${brandBlue}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.email ? '#DC2626' : '#E5E7EB';
              e.target.style.boxShadow = 'none';
            }}
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="mt-2 text-sm" style={{ color: '#DC2626' }}>{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label 
              htmlFor="password" 
              className="block text-sm font-medium"
              style={{ color: '#374151' }}
            >
              Password
            </label>
            <Link
              href="/forgot-password"
              className="text-sm font-medium transition-colors hover:underline"
              style={{ color: brandBlue }}
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              data-testid="login-password-input"
              {...register('password', {
                onChange: () => {
                  if (error) setError(null);
                }
              })}
              className="w-full px-4 py-3 pr-12 rounded-xl text-base transition-all duration-200"
              style={{
                backgroundColor: '#F9FAFB',
                border: errors.password ? '1px solid #DC2626' : '1px solid #E5E7EB',
                color: '#111827',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = brandBlue;
                e.target.style.boxShadow = `0 0 0 3px ${brandBlue}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.password ? '#DC2626' : '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: '#9CA3AF' }}
              onClick={() => setShowPassword(!showPassword)}
              onMouseEnter={(e) => e.currentTarget.style.color = '#6B7280'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-2 text-sm" style={{ color: '#DC2626' }}>{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="login-submit-button"
          className="w-full py-3 px-4 rounded-full text-base font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: brandBlue,
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.transform = 'scale(1.01)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2" data-testid="login-loading">
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Signing in...
            </span>
          ) : !appReady && !initError ? (
            <span className="flex items-center justify-center gap-2" data-testid="login-loading">
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Initializing...
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Don&apos;t have an account?{' '}
          <Link
            href="/get-started"
            className="font-medium transition-colors hover:underline"
            style={{ color: brandBlue }}
          >
            Get Started
          </Link>
        </p>
      </div>

      <div className="mt-6 text-center">
        <Link
          href="/home"
          className="inline-flex items-center gap-1.5 text-sm transition-colors hover:underline"
          style={{ color: '#9CA3AF' }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#6B7280'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </Link>
      </div>
    </>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}